import { FC } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ChevronRightIcon,
  ExclamationCircleIcon,
  AdjustmentsHorizontalIcon,
  HandThumbUpIcon
} from '@heroicons/react/24/outline'
import { InsightSubType } from 'models'
import { InsightsSummary } from '../../categoryDashboard.types'
import { InsightsWrapper, InsightSkeletonBar } from './topCategoryInsights.styles'
import { InsightsIcon } from '../icons'

interface Props {
  data?: InsightsSummary
  loading?: boolean
  cpis?: { name: string; label: string }[]
}

const TopCategoryInsights: FC<Props> = ({ data, loading, cpis }) => {
  const navigate = useNavigate()
  // Block navigation until the category data has resolved — clicking during
  // load would forward an empty `cpis` array, which the Insights page can't
  // distinguish from a category that genuinely has no CPIs scoped.
  const canNavigate = !loading

  // `types` scopes the destination to one insight kind, matching the dashboard's
  // OnyxAI Insights cards; the title chevron omits it to show the whole category.
  const goToInsights = (types?: InsightSubType[]) => {
    if (!canNavigate) return
    navigate('/insights', {
      state: {
        cpis: cpis ?? [],
        ...(types && { types }),
        time: 'Last Month',
        // Hard signal that this navigation is scoped to a single category.
        // The Insights page uses it to suppress unrelated insights even when
        // the category has no active CPIs (in which case `cpis` is empty and
        // the legacy filter-applied path silently falls back to "show all").
        categoryScoped: true
      }
    })
  }

  // Each card's sub types mirror how the category's insight counts are tallied in
  // useCategoryDashboardData, so the destination list matches the number shown.
  const cards = [
    {
      icon: <ExclamationCircleIcon />,
      label: 'Declining Trends',
      value: data?.performanceDegradation ?? 0,
      types: [InsightSubType.Reduction, InsightSubType.CrossedIntoRed]
    },
    {
      icon: <AdjustmentsHorizontalIcon />,
      label: 'Suggested Optimization',
      value: data?.suggestedOptimization ?? 0,
      types: [InsightSubType.CpiSlaOptimization]
    },
    {
      icon: <HandThumbUpIcon />,
      label: 'Improving Trends',
      value: data?.performanceImprovement ?? 0,
      types: [InsightSubType.Improvement, InsightSubType.CrossedIntoGreen]
    }
  ]

  return (
    <InsightsWrapper>
      <div className="section-header">
        <div className="header-left">
          <div className="title-row">
            <InsightsIcon className="title-icon" />
            <span className="title-text">Top Category Insights</span>
          </div>
          <span className="subtitle">Last 30 days</span>
        </div>
        <div
          className={`chevron-button ${canNavigate ? '' : 'disabled'}`}
          onClick={() => goToInsights()}
          aria-disabled={!canNavigate}
        >
          <ChevronRightIcon />
        </div>
      </div>
      <div className="insight-cards">
        {loading
          ? [0, 1, 2].map((i) => <InsightSkeletonBar key={i} />)
          : cards.map((card, index) => (
              <div className="insight-card" key={index} onClick={() => goToInsights(card.types)}>
                <div className="insight-icon-box">{card.icon}</div>
                <div className="insight-content">
                  <span className="insight-label">{card.label}</span>
                  <span className="insight-value">{card.value}</span>
                </div>
              </div>
            ))}
      </div>
    </InsightsWrapper>
  )
}

export default TopCategoryInsights
